import React, { useEffect, useMemo } from 'react'
import { useAppSelector } from 'store/hooks'
import useGetUserState from 'store/api/user/useGetUserState'
import { userApi } from 'store/api/user'
import { useIsTwoFaVisible } from 'utils/useIsTwoFaVisible'
import useRedirects from 'utils/useRedirects'
import { useSuccessfulRegistrationTracking } from 'utils/campaignTracking'
import useMaintenanceMode from './utils/useMaintenanceMode'
import { useDetectConfiguredDevice } from './utils/useDetectConfiguredDevice'
import { useGetNotificationsQuery } from 'store/api/notifications'
import { useGetProfilesQuery } from 'store/api/profiles'
import { useGetStatEndpointsQuery } from 'store/api/analytics'
import { TrayRenderProps, useTrayPresenter } from 'ui'
import NavMenuMobile from './NavBar/NavMenu.mobile'
import OrganizationNavBarContent from '../Organization/OrganizationNavBar/OrganizationNavBarContent'
import NavBarContent from './NavBar/NavBarContent'
import useClearImpersonationState from './utils/useClearImpersonationState'

export default function DashboardInitialization(): null {
  const userPk = useAppSelector(s => s.session.userPk)
  const { isOrganization, startPollingUser } = useGetUserState()
  const { data: userData } = userApi.endpoints.getUser.useQueryState('')
  const isConfirmEmailVisible = !!userData?.org?.PK && !userData?.email_status
  const isTwoFaVisible = useIsTwoFaVisible()
  const isMobileNavMenuOpen = useAppSelector(s => s.dashboard.isMobileNavMenuOpen)
  const clearImpersonationState = useClearImpersonationState()

  //handle redirects from old paths
  useRedirects()
  useSuccessfulRegistrationTracking()
  useMaintenanceMode()
  useDetectConfiguredDevice()

  useGetNotificationsQuery('', { skip: !userPk })
  useGetProfilesQuery('')
  useGetStatEndpointsQuery('', {
    skip: !userPk,
    refetchOnMountOrArgChange: true,
  })

  useEffect(() => {
    if (!isOrganization) {
      return
    }

    window.addEventListener('popstate', function () {
      clearImpersonationState()
    })

    return () => {
      window.addEventListener('popstate', () => {})
    }
  }, [clearImpersonationState, isOrganization])

  useEffect(() => {
    if (isConfirmEmailVisible && userData && !isTwoFaVisible) {
      startPollingUser(true)
    }

    return () => {
      startPollingUser(false)
    }
  }, [userData, isConfirmEmailVisible, startPollingUser, isTwoFaVisible])

  const navMobileTray = useMemo(
    () => ({
      id: 'NavMobileTray',
      renderTray: (props: TrayRenderProps) => (
        <NavMenuMobile {...props}>
          {isOrganization ? <OrganizationNavBarContent /> : <NavBarContent />}
        </NavMenuMobile>
      ),
      zIndexName: 'zIndex550',
    }),
    [isOrganization],
  )
  const { showTray: showMobileTray, hideTray: hideMobileTray } = useTrayPresenter(navMobileTray)

  useEffect(() => {
    isMobileNavMenuOpen ? showMobileTray() : hideMobileTray()
  }, [hideMobileTray, isMobileNavMenuOpen, showMobileTray])

  return null
}
